import { Injectable, inject, DOCUMENT } from '@angular/core';

import { ENV } from '@bodyanalytics/app-config';

export interface TenantBrandingTokensI {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logo: string | null;
  favicon: string | null;
  fontDisplay: string;
  fontBody: string;
  portalTitle: string | null;
}

/** Tenant identity resolved by the public by-slug branding endpoint. */
export interface TenantBrandingContextI {
  tenantId: string;
  slug: string;
}

const DEFAULTS: TenantBrandingTokensI = {
  primaryColor: '#0A3D5C',
  secondaryColor: '#ffffff',
  accentColor: '#D4860A',
  logo: null,
  favicon: null,
  fontDisplay: 'Fraunces',
  fontBody: 'Plus Jakarta Sans',
  portalTitle: null,
};

/**
 * Render-boundary allowlists (issue #965).
 *
 * This service is the point where tenant-controlled branding values are
 * written into the live DOM: CSS custom properties on <html>, the document
 * title, and a favicon <link href>. Even though the API now validates branding
 * on write, this layer must NOT trust the values it receives — a branding row
 * written before the server-side fix, or fetched via a path that bypasses the
 * validated PUT endpoint, could still contain a payload like
 * `expression(alert(1))`, `url(javascript:...)`, `</style>...`, or a
 * `javascript:` favicon URL.
 *
 * Strategy: validate every token against the shape we expect and fall back to
 * the design-system default if it does not match. We never interpolate a raw
 * branding string into a style/href. setProperty already uses the CSSOM (no
 * <style>-block string concatenation), so the only residual risk is a malicious
 * *value*; the regex allowlists below close that.
 */
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const ALLOWED_FONTS = ['Fraunces', 'Plus Jakarta Sans', 'Geist Mono'];

type RgbT = readonly number[];

const WHITE: RgbT = [255, 255, 255];
const BLACK: RgbT = [0, 0, 0];
/** Design-system dark ink ($color-dark) — the dark foreground option. */
const INK = '#0d1f2d';

/** Expects an already-sanitized `#rgb` / `#rrggbb` value. */
function hexToRgb(hex: string): RgbT {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map(c => c + c)
          .join('')
      : h;
  return [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16));
}

function rgbToHex(rgb: RgbT): string {
  return `#${rgb.map(c => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
}

function sameColor(a: RgbT, b: RgbT): boolean {
  return a.every((c, i) => c === b[i]);
}

/** Linear blend: `weight` = share of `b` (0 → a, 1 → b). */
function mix(a: RgbT, b: RgbT, weight: number): RgbT {
  return a.map((c, i) => c + (b[i] - c) * weight);
}

/** WCAG 2.x relative luminance. */
function luminance(rgb: RgbT): number {
  const [r, g, b] = rgb.map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(a: RgbT, b: RgbT): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** `rgb`, darkened in 10% steps until it reads as AA text on white. */
function readableOnWhite(rgb: RgbT): RgbT {
  for (let w = 0; w <= 1; w += 0.1) {
    const candidate = mix(rgb, BLACK, w);
    if (contrastRatio(candidate, WHITE) >= 4.5) return candidate;
  }
  return BLACK;
}

/** White or dark ink — whichever reads better on `bg`. */
function bestForeground(bg: RgbT): string {
  if (contrastRatio(WHITE, bg) >= 4.5) return '#ffffff';
  if (contrastRatio(hexToRgb(INK), bg) >= 4.5) return INK;
  // Mid-tones (L≈0.2) clear AA with neither white nor ink; pure black always
  // reaches ≥ 4.58:1 wherever white falls short, so it is the floor.
  return contrastRatio(WHITE, bg) >= contrastRatio(BLACK, bg) ? '#ffffff' : '#000000';
}

/**
 * Shared runtime-theming service — the SINGLE implementation for every portal
 * (formerly duplicated in `apps/front/admin/src/app/branding.service.ts`).
 *
 * Entry points:
 *   - `initBySlug(slug)` — app initializer, before auth. Calendar resolves the
 *                          slug from the `/t/:slug` URL prefix; admin passes
 *                          its build-time `environment.companyId`, which is a
 *                          tenant SLUG (e.g. 'abx'), not a tenant id. Returns
 *                          the resolved `{ tenantId, slug }`, or `null` on any
 *                          failure (unknown slug, network error) — in which
 *                          case the design-system defaults are applied and
 *                          the app boots exactly like the unbranded portal.
 *   - `init(tenantId)`   — after sign-in, once the caller's own tenant id is
 *                          known (admin: AdminAuthService; operator:
 *                          `GET /operator/me`).
 *   - `apply(tokens)`    — re-theme from tokens already in hand (admin
 *                          Branding page after a successful save).
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly env = inject<{ api: string }>(ENV);
  private readonly document = inject(DOCUMENT);

  /** Called by APP_INITIALIZER — resolves before first render.
   *  Uses native fetch (not HttpClient) to avoid deadlocking with the
   *  auth interceptor, which waits for Auth0 isLoading$ before it can
   *  attach a token — a circular dependency on first load. */
  async init(tenantId: string, options: { keepCurrentOnError?: boolean } = {}): Promise<boolean> {
    try {
      const response = await fetch(`${this.env.api}/tenants/${tenantId}/branding`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const branding = (await response.json()) as Partial<TenantBrandingTokensI>;
      this.apply(branding);
      return true;
    } catch (_err: unknown) {
      // Never throw. At boot, fall back to design-system defaults; after
      // sign-in (`keepCurrentOnError`), keep whatever theme is already applied
      // so a transient failure doesn't wipe the boot theme. Resolves `false`
      // so the caller can retry.
      if (!options.keepCurrentOnError) this.apply(DEFAULTS);
      return false;
    }
  }

  /** Slug variant for runtime multi-tenancy (`GET /tenants/by-slug/:slug/branding`).
   *  Same native-fetch/no-token posture as `init`. Never throws and never
   *  blocks bootstrap: any failure applies defaults and resolves `null`. */
  async initBySlug(slug: string): Promise<TenantBrandingContextI | null> {
    try {
      const response = await fetch(`${this.env.api}/tenants/by-slug/${encodeURIComponent(slug)}/branding`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = (await response.json()) as Partial<TenantBrandingTokensI> & {
        tenantId?: unknown;
        slug?: unknown;
      };
      this.apply(payload);
      return typeof payload.tenantId === 'string' && payload.tenantId !== ''
        ? { tenantId: payload.tenantId, slug }
        : null;
    } catch (_err: unknown) {
      this.apply(DEFAULTS);
      return null;
    }
  }

  /**
   * Write branding tokens into the live DOM. Public so a caller that already
   * holds fresh tokens (the admin Branding page after a successful save) can
   * re-theme the running app without a reload. Every value still passes the
   * render-boundary sanitizer — callers never get a raw-string path.
   */
  apply(tokens: Partial<Record<keyof TenantBrandingTokensI, string | null>>): void {
    const safe = this.sanitizeTokens(tokens);
    const root = this.document.documentElement;

    root.style.setProperty('--color-primary', safe.primaryColor);
    root.style.setProperty('--color-secondary', safe.secondaryColor);
    root.style.setProperty('--color-accent', safe.accentColor);
    this.applyDerivedColors(root, safe.primaryColor, safe.accentColor);
    root.style.setProperty('--font-display', `'${safe.fontDisplay}', serif`);
    root.style.setProperty('--font-body', `'${safe.fontBody}', sans-serif`);

    if (safe.portalTitle) {
      // textContent path — never bound via innerHTML, so HTML is inert. The
      // sanitizer also strips angle brackets defensively.
      this.document.title = safe.portalTitle;
    }

    if (safe.favicon) {
      const link: HTMLLinkElement =
        (this.document.querySelector("link[rel*='icon']") as HTMLLinkElement) ?? this.document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = safe.favicon;
      this.document.head.appendChild(link);
    }
  }

  /**
   * Shades and foregrounds that the stylesheet compiles from the DEFAULT brand
   * colors (`_colors.scss`). When a tenant keeps a default color, the inline
   * override is removed so the hand-tuned design-system value (and any
   * dark-mode override) wins. When a tenant picks its own color, the shades
   * are derived from it and the foregrounds are chosen for contrast, so a
   * light primary never leaves white nav text on a light bar.
   */
  private applyDerivedColors(root: HTMLElement, primary: string, accent: string): void {
    const primaryRgb = hexToRgb(primary);
    const accentRgb = hexToRgb(accent);
    const primaryIsDefault = sameColor(primaryRgb, hexToRgb(DEFAULTS.primaryColor));
    const accentIsDefault = sameColor(accentRgb, hexToRgb(DEFAULTS.accentColor));
    const set = (name: string, value: string | null): void => {
      if (value === null) root.style.removeProperty(name);
      else root.style.setProperty(name, value);
    };

    set('--color-primary-light', primaryIsDefault ? null : rgbToHex(mix(primaryRgb, WHITE, 0.15)));
    set('--color-primary-deep', primaryIsDefault ? null : rgbToHex(mix(primaryRgb, BLACK, 0.55)));
    set('--color-primary-mid', primaryIsDefault ? null : rgbToHex(mix(primaryRgb, WHITE, 0.1)));
    set('--color-on-primary', primaryIsDefault ? null : bestForeground(primaryRgb));
    // Dimmed nav text on the bar is an alpha mix of --color-on-primary, which
    // can drop a tenant colour below AA — tenant brands get the solid colour.
    set('--color-on-primary-muted', primaryIsDefault ? null : bestForeground(primaryRgb));
    set('--color-primary-text', primaryIsDefault ? null : rgbToHex(readableOnWhite(primaryRgb)));

    const accentLight = mix(accentRgb, WHITE, 0.2);
    set('--color-accent-light', accentIsDefault ? null : rgbToHex(accentLight));
    set('--color-on-accent', accentIsDefault ? null : bestForeground(accentRgb));

    // Accent-coloured TEXT on a primary-coloured surface (active nav link).
    // First of light-accent / accent that clears WCAG AA on the primary,
    // else the plain on-primary foreground.
    const onDark =
      [accentLight, accentRgb].find(c => contrastRatio(c, primaryRgb) >= 4.5) ?? hexToRgb(bestForeground(primaryRgb));
    set('--color-accent-on-dark', primaryIsDefault && accentIsDefault ? null : rgbToHex(onDark));
  }

  /**
   * Coerce arbitrary (possibly hostile) branding values into the safe shapes
   * the renderer expects. Anything that fails validation is replaced with the
   * design-system default rather than rendered.
   */
  private sanitizeTokens(tokens: Partial<Record<keyof TenantBrandingTokensI, string | null>>): TenantBrandingTokensI {
    return {
      primaryColor: this.safeColor(tokens.primaryColor, DEFAULTS.primaryColor),
      secondaryColor: this.safeColor(tokens.secondaryColor, DEFAULTS.secondaryColor),
      accentColor: this.safeColor(tokens.accentColor, DEFAULTS.accentColor),
      logo: this.safeHttpsUrl(tokens.logo),
      favicon: this.safeHttpsUrl(tokens.favicon),
      fontDisplay: this.safeFont(tokens.fontDisplay, DEFAULTS.fontDisplay),
      fontBody: this.safeFont(tokens.fontBody, DEFAULTS.fontBody),
      portalTitle: this.safeTitle(tokens.portalTitle),
    };
  }

  private safeColor(value: string | null | undefined, fallback: string): string {
    return typeof value === 'string' && HEX_COLOR_REGEX.test(value.trim()) ? value.trim() : fallback;
  }

  private safeFont(value: string | null | undefined, fallback: string): string {
    return typeof value === 'string' && ALLOWED_FONTS.includes(value.trim()) ? value.trim() : fallback;
  }

  /** Only https URLs are allowed for asset references — blocks `javascript:`,
   *  `data:`, and any other script-capable scheme. */
  private safeHttpsUrl(value: string | null | undefined): string | null {
    if (typeof value !== 'string' || value.trim() === '') return null;
    try {
      const url = new URL(value.trim());
      return url.protocol === 'https:' ? url.href : null;
    } catch {
      return null;
    }
  }

  /** Plain text only — strip angle brackets so a stored payload cannot become
   *  HTML even if a future caller mistakenly binds the title via innerHTML. */
  private safeTitle(value: string | null | undefined): string | null {
    if (typeof value !== 'string') return null;
    const cleaned = value.replace(/[<>]/g, '').trim().slice(0, 120);
    return cleaned === '' ? null : cleaned;
  }
}
